import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import MarkdownComponent from './MarkdownComponent';
import { doc, getDoc, getDocs, collection, query, orderBy } from 'firebase/firestore';
import { db } from './firebase';
import { useRecordContext, useNotify, usePermissions, useCreate, useUpdate, useGetList } from 'react-admin';
import {
	Alert,
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	FormControlLabel,
	IconButton,
	InputLabel,
	ListItemText,
	MenuItem,
	Select,
	Switch,
	Tab,
	Tabs,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import FolderIcon from '@mui/icons-material/Folder';
import GitHubIcon from '@mui/icons-material/GitHub';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
	deleteFile,
	getFile,
	listDirectory,
	putTextFile,
	uploadBinary,
} from './githubApi';

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useGithubConfig() {
	const [ghConfig, setGhConfig] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	useEffect(() => {
		let cancelled = false;
		getDoc(doc(db, 'settings', 'github'))
			.then(snap => {
				if (cancelled) return;
				if (snap.exists() && snap.data().pat) {
					setGhConfig(snap.data());
				} else {
					setError('not-configured');
				}
			})
			.catch(e => { if (!cancelled) setError(e.message); })
			.finally(() => { if (!cancelled) setLoading(false); });
		return () => { cancelled = true; };
	}, []);

	return { ghConfig, loading, error };
}

// ---------------------------------------------------------------------------
// Sync: regenerate projectsConfig.json from Firestore (Firestore is master)
// ---------------------------------------------------------------------------

export async function syncProjectsConfig(ghConfig) {
	const { pat, owner, repo, branch } = ghConfig;
	const snap = await getDocs(query(collection(db, 'projects'), orderBy('year', 'desc')));
	const projects = snap.docs
		.filter(d => d.data().nameid)
		.map((d, idx) => {
			const data = d.data();
			return {
				id: idx,
				visibility: data.visibility ?? true,
				nameid: data.nameid,
				image: data.image ?? '',
				year: String(data.year ?? new Date().getFullYear()),
				title: data.name ?? data.nameid,
				description: data.description ?? '',
				gallery: data.gallery ?? [],
				buttonText: data.buttonText ?? 'MoreInfo',
				markdownFile: data.markdownFile ?? `${data.nameid}.md`,
				services: data.services ?? [],
				model_full: data.model_full ?? '',
			};
		});
	let sha = null;
	try { ({ sha } = await getFile(pat, owner, repo, 'projects/projectsConfig.json', branch)); } catch {}
	await putTextFile(pat, owner, repo, 'projects/projectsConfig.json', branch,
		JSON.stringify(projects, null, 4), sha, '[admin] sync projectsConfig.json from Firestore');
	return projects;
}

function useProjectEntry(ghConfig, nameid) {
	const [entry, setEntry] = useState(null);
	const [allProjects, setAllProjects] = useState(null);
	const [configSha, setConfigSha] = useState(null);
	const [loading, setLoading] = useState(true);
	const notify = useNotify();

	const load = useCallback(async () => {
		if (!ghConfig) return;
		setLoading(true);
		try {
			const { content, sha } = await getFile(
				ghConfig.pat, ghConfig.owner, ghConfig.repo,
				'projects/projectsConfig.json', ghConfig.branch
			);
			const projects = JSON.parse(content);
			setAllProjects(projects);
			setConfigSha(sha);
			setEntry(projects.find(p => p.nameid === nameid) ?? null);
		} catch (e) {
			notify(`Could not load projectsConfig.json: ${e.message}`, { type: 'error' });
		}
		setLoading(false);
	}, [ghConfig, nameid, notify]);

	useEffect(() => { load(); }, [load]);

	return { entry, allProjects, configSha, loading, reload: load };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const IMAGE_EXTS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'avif', 'bmp']);
const isImage = name => IMAGE_EXTS.has((name.split('.').pop() ?? '').toLowerCase());

// Fetches an image via the GitHub API using the stored PAT, then renders it
// from a local blob URL. This works for both public and private repos and
// avoids raw.githubusercontent.com authentication issues.
export function GitHubImage({ pat, owner, repo, branch, fallbackBranch, filePath, alt, imgStyle, placeholder }) {
	const [blobUrl, setBlobUrl] = useState(null);
	const [failed, setFailed] = useState(false);
	const activeUrlRef = useRef(null);

	// Revoke blob URL on unmount only — not on every effect re-run
	useEffect(() => {
		return () => {
			if (activeUrlRef.current?.startsWith('blob:')) URL.revokeObjectURL(activeUrlRef.current);
		};
	}, []);

	useEffect(() => {
		let cancelled = false;
		const controller = new AbortController();
		setFailed(false);
		// Don't clear blobUrl here — keep previous image visible while re-fetching

		const fetchRef = (ref) =>
			fetch(`https://api.github.com/repos/${owner}/${repo}/contents/${filePath}?ref=${ref}`, {
				signal: controller.signal,
				headers: {
					Authorization: `Bearer ${pat}`,
					Accept: 'application/vnd.github+json',
					'X-GitHub-Api-Version': '2022-11-28',
				},
			}).then(r => { if (!r.ok) throw new Error(r.status); return r.json(); });

		fetchRef(branch)
			.catch(() => (fallbackBranch && fallbackBranch !== branch) ? fetchRef(fallbackBranch) : Promise.reject())
			.then(data => {
				if (cancelled) return;
				if (activeUrlRef.current?.startsWith('blob:')) URL.revokeObjectURL(activeUrlRef.current);
				if (data.content && data.encoding !== 'none') {
					// Small file (<1 MB): decode inline base64
					const binary = atob(data.content.replace(/\n/g, ''));
					const bytes = new Uint8Array(binary.length);
					for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
					activeUrlRef.current = URL.createObjectURL(new Blob([bytes]));
				} else if (data.download_url) {
					// Large file (>1 MB): GitHub omits content; use raw download URL directly.
					// Works for public repos without additional auth.
					activeUrlRef.current = data.download_url;
				} else {
					throw new Error('no content');
				}
				setBlobUrl(activeUrlRef.current);
			})
			.catch(() => {
				// A superseded request (aborted because deps changed, or a
				// React 18 StrictMode dev double-invoke) landing after the
				// current one was previously able to blank out an
				// already-loaded image. Aborted fetches never reach here, and
				// `cancelled` covers the rest.
				if (!cancelled) {
					setBlobUrl(null);
					setFailed(true);
				}
			});

		return () => {
			cancelled = true;
			controller.abort();
		};
	}, [pat, owner, repo, branch, fallbackBranch, filePath]);

	const fallback = placeholder ?? (
		<Box sx={{ ...imgStyle, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
			<Typography variant="caption" color="text.disabled" sx={{ fontSize: 9 }}>
				{failed ? 'not found' : '…'}
			</Typography>
		</Box>
	);

	if (failed || !blobUrl) return fallback;
	return <img src={blobUrl} alt={alt ?? filePath} style={imgStyle} />;
}

const LANGS = ['en', 'it', 'es', 'fr', 'de'];

const mdTemplate = title => `[TA-CENTER]

# #a9d734#${title}

---local(en)

Enter project description in English.

---local(it)

Inserisci la descrizione del progetto in italiano.

---local(es)

Ingresa la descripción del proyecto en español.

---local(fr)

Entrez la description du projet en français.

---local(de)

Geben Sie die Projektbeschreibung auf Deutsch ein.

---

`;

// ---------------------------------------------------------------------------
// Markdown preview — real render, language-switchable
// ---------------------------------------------------------------------------

function MarkdownPreview({ markdown }) {
	const { i18n } = useTranslation();
	const originalLang = useRef(i18n.language);
	const [previewLang, setPreviewLang] = useState(i18n.language.slice(0, 2));

	const switchLang = useCallback(lang => {
		setPreviewLang(lang);
		i18n.changeLanguage(lang);
	}, [i18n]);

	useEffect(() => {
		const orig = originalLang.current;
		return () => { i18n.changeLanguage(orig); };
	}, [i18n]);

	return (
		<Box display="flex" flexDirection="column" height="100%">
			<Box display="flex" gap={0.5} mb={0.75} flexWrap="wrap">
				{LANGS.map(lang => (
					<Chip
						key={lang}
						label={lang}
						size="small"
						variant={previewLang === lang ? 'filled' : 'outlined'}
						color={previewLang === lang ? 'primary' : 'default'}
						onClick={() => switchLang(lang)}
						sx={{ cursor: 'pointer', fontSize: 11, height: 20 }}
					/>
				))}
			</Box>
			<Box sx={{ flex: 1, overflow: 'auto' }}>
				<MarkdownComponent markdown={markdown} />
			</Box>
		</Box>
	);
}

// ---------------------------------------------------------------------------
// Markdown editor
// ---------------------------------------------------------------------------

function MarkdownEditor({ nameid, markdownFile, ghConfig }) {
	const notify = useNotify();
	const [content, setContent] = useState('');
	const [sha, setSha] = useState(null);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const { pat, owner, repo, branch } = ghConfig;
	const filePath = `projects/${nameid}/${markdownFile}`;

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const { content: text, sha: s } = await getFile(pat, owner, repo, filePath, branch);
			setContent(text);
			setSha(s);
		} catch (e) {
			notify(`Could not load file: ${e.message}`, { type: 'error' });
		}
		setLoading(false);
	}, [pat, owner, repo, filePath, branch, notify]);

	useEffect(() => { load(); }, [load]);

	const save = async () => {
		setSaving(true);
		try {
			const res = await putTextFile(pat, owner, repo, filePath, branch, content, sha, `[admin] update ${markdownFile}`);
			setSha(res.content.sha);
			notify('Saved to content branch — redeploy to publish.', { type: 'success' });
		} catch (e) {
			notify(`Save failed: ${e.message}`, { type: 'error' });
		}
		setSaving(false);
	};

	if (loading) return <CircularProgress />;

	return (
		<Box>
			<Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
				<Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
					{branch}/{filePath}
				</Typography>
				<Box display="flex" gap={1} alignItems="center">
					<Tooltip title="Reload from GitHub">
						<IconButton size="small" onClick={load} disabled={loading}>
							<RefreshIcon fontSize="small" />
						</IconButton>
					</Tooltip>
					<Button variant="contained" size="small" startIcon={<SaveIcon />} onClick={save} disabled={saving}>
						{saving ? 'Saving…' : 'Save'}
					</Button>
				</Box>
			</Box>

			<Alert severity="info" sx={{ mb: 1, fontSize: 12 }}>
				<strong>Syntax:</strong>{' '}
				<code>---local(en)</code> lang blocks &nbsp;·&nbsp;
				<code>?#a9d734?text?#a9d734?</code> color &nbsp;·&nbsp;
				<code>[TA-CENTER]</code> align &nbsp;·&nbsp;
				<code>[button:Label](url)</code> &nbsp;·&nbsp;
				<code>[download-email:Label](url)</code> &nbsp;·&nbsp;
				<code>---v</code> column split &nbsp;·&nbsp;
				table <code>colspan=N</code> / <code>!null!</code>
			</Alert>

			<Box display="flex" gap={2} sx={{ height: '62vh' }}>
				<Box flex={1} display="flex" flexDirection="column">
					<Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>Source</Typography>
					<textarea
						value={content}
						onChange={e => setContent(e.target.value)}
						spellCheck={false}
						style={{
							flex: 1,
							fontFamily: 'monospace',
							fontSize: 13,
							padding: 8,
							border: '1px solid #ddd',
							borderRadius: 4,
							resize: 'none',
							lineHeight: 1.5,
							outline: 'none',
						}}
					/>
				</Box>

				<Divider orientation="vertical" flexItem />

				<Box flex={1} display="flex" flexDirection="column" overflow="hidden">
					<Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>Preview</Typography>
					<Box
						sx={{
							flex: 1,
							overflow: 'auto',
							border: '1px solid #ddd',
							borderRadius: 1,
							p: 1,
							bgcolor: '#ffffff',
							color: '#333333',
							colorScheme: 'light',
						}}
					>
						<MarkdownPreview markdown={content} />
					</Box>
				</Box>
			</Box>
		</Box>
	);
}

// ---------------------------------------------------------------------------
// File browser (replaces old image-only gallery)
// Images are at projects/${nameid}/ root — NOT in an image/ subfolder.
// ---------------------------------------------------------------------------

function FileBrowser({ nameid, ghConfig }) {
	const notify = useNotify();
	const { pat, owner, repo, branch } = ghConfig;
	const rootPath = `projects/${nameid}`;
	const [currentPath, setCurrentPath] = useState(rootPath);
	const [items, setItems] = useState([]);
	const [loading, setLoading] = useState(true);
	const [uploading, setUploading] = useState(false);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const result = await listDirectory(pat, owner, repo, currentPath, branch);
			setItems(Array.isArray(result) ? result : []);
		} catch (e) {
			if (e.message.includes('404')) setItems([]);
			else notify(`Could not list ${currentPath}: ${e.message}`, { type: 'error' });
		}
		setLoading(false);
	}, [pat, owner, repo, currentPath, branch, notify]);

	useEffect(() => { load(); }, [load]);

	const navigateInto = dirName => setCurrentPath(p => `${p}/${dirName}`);
	const navigateUp = () => setCurrentPath(p => {
		const parts = p.split('/');
		return parts.length > rootPath.split('/').length ? parts.slice(0, -1).join('/') : p;
	});
	const isAtRoot = currentPath === rootPath;

	const [uploadProgress, setUploadProgress] = useState(null); // "2 / 5"

	const handleUpload = async e => {
		const files = Array.from(e.target.files);
		if (!files.length) return;
		setUploading(true);
		let failed = 0;
		for (let i = 0; i < files.length; i++) {
			const file = files[i];
			setUploadProgress(`${i + 1} / ${files.length}`);
			try {
				const buf = await file.arrayBuffer();
				const existingSha = items.find(item => item.name === file.name)?.sha ?? null;
				await uploadBinary(pat, owner, repo, `${currentPath}/${file.name}`, branch, buf, existingSha, `[admin] upload ${file.name}`);
			} catch {
				failed++;
			}
		}
		if (failed) notify(`${failed} file(s) failed to upload`, { type: 'error' });
		else notify(`Uploaded ${files.length} file(s)`, { type: 'success' });
		setUploadProgress(null);
		setUploading(false);
		e.target.value = '';
		await load();
	};

	const handleDelete = async item => {
		if (!window.confirm(`Delete ${item.name}?`)) return;
		try {
			await deleteFile(
				pat, owner, repo,
				`${currentPath}/${item.name}`,
				branch, item.sha,
				`[admin] delete ${item.name}`
			);
			notify(`Deleted ${item.name}`, { type: 'success' });
			await load();
		} catch (e) {
			notify(`Delete failed: ${e.message}`, { type: 'error' });
		}
	};

	const dirs = items.filter(i => i.type === 'dir');
	const images = items.filter(i => i.type === 'file' && isImage(i.name));
	const otherFiles = items.filter(i => i.type === 'file' && !isImage(i.name));
	const breadcrumb = currentPath.split('/').slice(1).join(' / ');

	return (
		<Box>
			{/* Toolbar */}
			<Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
				<Box display="flex" alignItems="center" gap={1}>
					{!isAtRoot && (
						<Tooltip title="Up one level">
							<IconButton size="small" onClick={navigateUp}>
								<ArrowBackIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					)}
					<Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
						{breadcrumb}
					</Typography>
				</Box>
				<Box display="flex" gap={1}>
					<Tooltip title="Refresh">
						<IconButton size="small" onClick={load}><RefreshIcon fontSize="small" /></IconButton>
					</Tooltip>
					<label>
						<input type="file" multiple style={{ display: 'none' }} onChange={handleUpload} disabled={uploading} />
						<Button variant="outlined" size="small" component="span" startIcon={<UploadFileIcon />} disabled={uploading}>
							{uploadProgress ? `Uploading ${uploadProgress}…` : 'Upload here'}
						</Button>
					</label>
				</Box>
			</Box>

			{loading ? <CircularProgress /> : (
				<Box>
					{/* Folders */}
					{dirs.length > 0 && (
						<Box mb={2}>
							<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
								Folders
							</Typography>
							<Box display="flex" flexWrap="wrap" gap={1}>
								{dirs.map(d => (
									<Button
										key={d.name}
										size="small"
										variant="outlined"
										startIcon={<FolderIcon />}
										onClick={() => navigateInto(d.name)}
										sx={{ textTransform: 'none', fontSize: 12 }}
									>
										{d.name}
									</Button>
								))}
							</Box>
						</Box>
					)}

					{/* Images */}
					{images.length > 0 && (
						<Box mb={2}>
							<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
								Images ({images.length})
							</Typography>
							<Box display="flex" flexWrap="wrap" gap={1.5}>
								{images.map(f => (
									<Box
										key={f.name}
										sx={{
											position: 'relative', width: 140,
											border: '1px solid #eee', borderRadius: 1, overflow: 'hidden',
											'&:hover .del': { opacity: 1 },
										}}
									>
										<GitHubImage
											pat={pat} owner={owner} repo={repo} branch={branch}
											filePath={`${currentPath}/${f.name}`}
											alt={f.name}
											imgStyle={{ width: '100%', height: 90, objectFit: 'cover', display: 'block' }}
										/>
										<Box px={0.5} pb={0.5}>
											<Typography variant="caption" sx={{ display: 'block', wordBreak: 'break-all', fontSize: 10 }}>
												{f.name}
											</Typography>
										</Box>
										<IconButton
											className="del"
											size="small"
											onClick={() => handleDelete(f)}
											sx={{
												position: 'absolute', top: 2, right: 2,
												bgcolor: 'rgba(255,255,255,0.85)',
												opacity: 0, transition: 'opacity 0.15s', p: 0.5,
											}}
										>
											<DeleteIcon fontSize="small" color="error" />
										</IconButton>
									</Box>
								))}
							</Box>
						</Box>
					)}

					{/* Other files */}
					{otherFiles.length > 0 && (
						<Box>
							<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
								Other files ({otherFiles.length})
							</Typography>
							<Box display="flex" flexWrap="wrap" gap={1}>
								{otherFiles.map(f => (
									<Box
										key={f.name}
										sx={{
											display: 'flex', alignItems: 'center', gap: 0.5,
											border: '1px solid #eee', borderRadius: 1, px: 1, py: 0.5,
											'&:hover .del': { opacity: 1 },
										}}
									>
										<InsertDriveFileIcon fontSize="small" color="action" />
										<Typography variant="caption" sx={{ fontSize: 11 }}>{f.name}</Typography>
										<IconButton
											className="del"
											size="small"
											onClick={() => handleDelete(f)}
											sx={{ opacity: 0, transition: 'opacity 0.15s', p: 0.25, ml: 0.5 }}
										>
											<DeleteIcon fontSize="small" color="error" />
										</IconButton>
									</Box>
								))}
							</Box>
						</Box>
					)}

					{items.length === 0 && (
						<Typography variant="body2" color="text.secondary">Empty folder.</Typography>
					)}
				</Box>
			)}
		</Box>
	);
}

// ---------------------------------------------------------------------------
// Image picker — loads available images from a folder, click to select
// ---------------------------------------------------------------------------

function ImagePickerGrid({ pat, owner, repo, branch, dirPath, value, onChange, multiple }) {
	const [images, setImages] = useState([]);
	const [loading, setLoading] = useState(true);

	const load = useCallback(() => {
		setLoading(true);
		listDirectory(pat, owner, repo, dirPath, branch)
			.then(items => setImages((Array.isArray(items) ? items : []).filter(i => i.type === 'file' && isImage(i.name))))
			.catch(() => setImages([]))
			.finally(() => setLoading(false));
	}, [pat, owner, repo, branch, dirPath]);

	useEffect(() => { load(); }, [load]);

	const selected = multiple ? (value ?? []) : (value ? [value] : []);

	const toggle = name => {
		if (multiple) {
			const arr = value ?? [];
			onChange(arr.includes(name) ? arr.filter(n => n !== name) : [...arr, name]);
		} else {
			onChange(selected[0] === name ? '' : name);
		}
	};

	if (loading) return <CircularProgress size={18} />;
	if (images.length === 0) return (
		<Box display="flex" alignItems="center" gap={1}>
			<Typography variant="caption" color="text.secondary">No images in folder yet — upload some in the Files tab.</Typography>
			<IconButton size="small" onClick={load}><RefreshIcon fontSize="small" /></IconButton>
		</Box>
	);

	return (
		<Box>
			<Box display="flex" flexWrap="wrap" gap={1.5}>
				{images.map(img => {
					const isSel = selected.includes(img.name);
					return (
						<Box key={img.name} onClick={() => toggle(img.name)} sx={{
							position: 'relative', width: 110, cursor: 'pointer', borderRadius: 1, overflow: 'hidden',
							border: isSel ? '2px solid #1976d2' : '2px solid #e0e0e0',
							'&:hover': { borderColor: '#1976d2' },
							transition: 'border-color 0.12s',
						}}>
							<GitHubImage pat={pat} owner={owner} repo={repo} branch={branch}
								filePath={`${dirPath}/${img.name}`} alt={img.name}
								imgStyle={{ width: '100%', height: 70, objectFit: 'cover', display: 'block' }} />
							{isSel && (
								<Box sx={{
									position: 'absolute', top: 3, right: 3, bgcolor: '#1976d2',
									borderRadius: '50%', width: 20, height: 20,
									display: 'flex', alignItems: 'center', justifyContent: 'center',
								}}>
									<CheckIcon sx={{ fontSize: 14, color: '#fff' }} />
								</Box>
							)}
							<Box px={0.5} py={0.25} sx={{ bgcolor: '#fafafa' }}>
								<Typography variant="caption" sx={{ display: 'block', wordBreak: 'break-all', fontSize: 9, lineHeight: 1.2 }}>
									{img.name}
								</Typography>
							</Box>
						</Box>
					);
				})}
			</Box>
			<Tooltip title="Refresh after uploading new files">
				<IconButton size="small" onClick={load} sx={{ mt: 0.5 }}><RefreshIcon fontSize="small" /></IconButton>
			</Tooltip>
		</Box>
	);
}

// ---------------------------------------------------------------------------
// Project config editor — reads from Firestore record, saves to Firestore,
// then regenerates projectsConfig.json so Firestore is the single master.
// ---------------------------------------------------------------------------

function ProjectConfigEditor({ ghConfig }) {
	const record = useRecordContext();
	const notify = useNotify();
	const [update] = useUpdate();
	const { pat, owner, repo, branch } = ghConfig;
	const nameid = record?.nameid;

	const [fields, setFields] = useState({
		visibility: record?.visibility ?? true,
		image: record?.image ?? '',
		gallery: record?.gallery ?? [],
		markdownFile: record?.markdownFile ?? `${nameid}.md`,
	});
	const [saving, setSaving] = useState(false);

	// Keep local state in sync if record changes (e.g. after save elsewhere)
	useEffect(() => {
		if (record) setFields({
			visibility: record.visibility ?? true,
			image: record.image ?? '',
			gallery: record.gallery ?? [],
			markdownFile: record.markdownFile ?? `${record.nameid}.md`,
		});
	}, [record]);

	const set = (key, val) => setFields(f => ({ ...f, [key]: val }));

	const save = async () => {
		if (!record?.id) return;
		setSaving(true);
		try {
			await update('projects', { id: record.id, data: fields, previousData: record });
			await syncProjectsConfig(ghConfig);
			notify('Saved to Firestore and synced to site.', { type: 'success' });
		} catch (e) {
			notify(`Save failed: ${e.message}`, { type: 'error' });
		}
		setSaving(false);
	};

	if (!record) return null;

	return (
		<Box>
			<Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
				<Typography variant="caption" color="text.secondary">
					Saved to Firestore → auto-syncs to site content
				</Typography>
				<Button variant="contained" size="small" startIcon={<SaveIcon />} onClick={save} disabled={saving}>
					{saving ? 'Saving & syncing…' : 'Save & sync'}
				</Button>
			</Box>

			<Box display="flex" flexDirection="column" gap={2}>
				<Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
					<FormControlLabel
						control={<Switch checked={fields.visibility ?? true} onChange={e => set('visibility', e.target.checked)} color="success" />}
						label="Visible on site"
					/>
					<TextField label="Markdown file" value={fields.markdownFile ?? ''} onChange={e => set('markdownFile', e.target.value)} size="small" sx={{ flex: 1, minWidth: 160 }} />
				</Box>

				<Box>
					<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
						Main image {fields.image ? <strong>({fields.image})</strong> : '— click to select'}
					</Typography>
					<ImagePickerGrid pat={pat} owner={owner} repo={repo} branch={branch}
						dirPath={`projects/${nameid}`}
						value={fields.image ?? ''}
						onChange={v => set('image', v)}
						multiple={false} />
				</Box>

				<Box>
					<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
						Gallery — {(fields.gallery ?? []).length} selected &nbsp;
						<span style={{ opacity: 0.6 }}>click to add/remove</span>
					</Typography>
					<ImagePickerGrid pat={pat} owner={owner} repo={repo} branch={branch}
						dirPath={`projects/${nameid}`}
						value={fields.gallery ?? []}
						onChange={v => set('gallery', v)}
						multiple />
				</Box>
			</Box>
		</Box>
	);
}

// ---------------------------------------------------------------------------
// New project dialog — exported, used from ProjectsList
// Creates content branch structure + optional Firestore record in one step
// ---------------------------------------------------------------------------

export function NewProjectDialog({ open, onClose }) {
	const { ghConfig, loading: configLoading } = useGithubConfig();
	const notify = useNotify();
	const [create] = useCreate();
	const [creating, setCreating] = useState(false);
	const { data: servicesData } = useGetList('services', {
		pagination: { page: 1, perPage: 100 },
		sort: { field: 'order', order: 'ASC' },
	});
	const serviceChoices = servicesData ?? [];
	const empty = { nameid: '', title: '', description: '', year: String(new Date().getFullYear()), visibility: true, services: [], createFirestore: true };
	const [fields, setFields] = useState(empty);

	const set = (key, val) => setFields(prev => ({ ...prev, [key]: val }));

	const handleCreate = async () => {
		if (!fields.nameid || !fields.title || !ghConfig) return;
		setCreating(true);
		const { pat, owner, repo, branch } = ghConfig;
		try {
			const { content, sha: configSha } = await getFile(pat, owner, repo, 'projects/projectsConfig.json', branch);
			const allProjects = JSON.parse(content);

			if (allProjects.find(p => p.nameid === fields.nameid)) {
				// Content already exists — only create the Firestore record if requested
				if (fields.createFirestore) {
					await create('projects', {
						data: {
							name: fields.title,
							nameid: fields.nameid,
							description: fields.description,
							year: parseInt(fields.year, 10) || new Date().getFullYear(),
							model_full: '',
							systems: [],
							configurations: [],
							services: fields.services,
						},
					});
					notify(`"${fields.title}" added to admin database — content already exists on branch.`, { type: 'success' });
					setFields(empty);
					onClose();
				} else {
					notify(`nameid "${fields.nameid}" already exists in projectsConfig.json.`, { type: 'warning' });
				}
				setCreating(false);
				return;
			}

			const markdownFile = `${fields.nameid}.md`;
			const newEntry = {
				id: allProjects.length,
				visibility: fields.visibility,
				nameid: fields.nameid,
				image: '',
				year: fields.year,
				services: fields.services,
				title: fields.title,
				description: fields.description,
				gallery: [],
				buttonText: 'MoreInfo',
				markdownFile,
				markdownFiles: [],
			};

			await putTextFile(
				pat, owner, repo,
				'projects/projectsConfig.json',
				branch,
				JSON.stringify([...allProjects, newEntry], null, 4),
				configSha,
				`[admin] add project ${fields.nameid}`
			);

			await putTextFile(
				pat, owner, repo,
				`projects/${fields.nameid}/${markdownFile}`,
				branch,
				mdTemplate(fields.title),
				null,
				`[admin] initialize ${fields.nameid}/${markdownFile}`
			);

			if (fields.createFirestore) {
				await create('projects', {
					data: {
						name: fields.title,
						nameid: fields.nameid,
						description: fields.description,
						year: parseInt(fields.year, 10) || new Date().getFullYear(),
						model_full: '',
						systems: [],
						configurations: [],
						services: fields.services,
					},
				});
			}

			notify(`Project "${fields.title}" created — redeploy to publish.`, { type: 'success' });
			setFields(empty);
			onClose();
		} catch (e) {
			notify(`Failed: ${e.message}`, { type: 'error' });
		}
		setCreating(false);
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>New Project</DialogTitle>
			<DialogContent>
				{configLoading ? (
					<CircularProgress />
				) : !ghConfig ? (
					<Alert severity="warning">GitHub settings not configured — go to Settings → GitHub first.</Alert>
				) : (
					<Box display="flex" flexDirection="column" gap={2} pt={1}>
						<TextField
							label="ID (nameid)"
							value={fields.nameid}
							onChange={e => set('nameid', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
							size="small"
							fullWidth
							required
							helperText="Lowercase letters, numbers, hyphens only — used as folder name"
						/>
						<TextField
							label="Title"
							value={fields.title}
							onChange={e => set('title', e.target.value)}
							size="small"
							fullWidth
							required
						/>
						<TextField
							label="Description (card subtitle)"
							value={fields.description}
							onChange={e => set('description', e.target.value)}
							size="small"
							fullWidth
						/>
						<Box display="flex" gap={2}>
							<TextField
								label="Year"
								value={fields.year}
								onChange={e => set('year', e.target.value)}
								size="small"
								sx={{ width: 120 }}
							/>
							<FormControl size="small" sx={{ flex: 1 }}>
								<InputLabel>Services</InputLabel>
								<Select
									multiple
									value={fields.services}
									onChange={e => set('services', e.target.value)}
									label="Services"
									renderValue={selected => (
										<Box display="flex" flexWrap="wrap" gap={0.5}>
											{selected.map(id => {
												const svc = serviceChoices.find(s => s.id === id);
												return <Chip key={id} label={svc?.name ?? id} size="small" />;
											})}
										</Box>
									)}
								>
									{serviceChoices.map(s => (
										<MenuItem key={s.id} value={s.id}>
											<Checkbox checked={fields.services.includes(s.id)} size="small" />
											<ListItemText primary={s.name} />
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Box>
						<Box display="flex" gap={3} flexWrap="wrap">
							<FormControlLabel
								control={<Switch checked={fields.visibility} onChange={e => set('visibility', e.target.checked)} color="success" />}
								label="Visible on site"
							/>
							<FormControlLabel
								control={<Switch checked={fields.createFirestore} onChange={e => set('createFirestore', e.target.checked)} />}
								label="Also create in Admin database"
							/>
						</Box>
					</Box>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={creating}>Cancel</Button>
				<Button
					variant="contained"
					onClick={handleCreate}
					disabled={creating || !fields.nameid || !fields.title || !ghConfig}
					startIcon={creating ? <CircularProgress size={14} /> : <AddIcon />}
				>
					{creating ? 'Creating…' : 'Create project'}
				</Button>
			</DialogActions>
		</Dialog>
	);
}

// ---------------------------------------------------------------------------
// Main export — tab dropped into ProjectsEdit / ProjectShow
// ---------------------------------------------------------------------------

export const ContentEditorTab = () => {
	const record = useRecordContext();
	const { permissions } = usePermissions();
	const [activeTab, setActiveTab] = useState(0);

	const { ghConfig, loading: configLoading, error: configError } = useGithubConfig();

	if (!record?.nameid) return null;
	if (permissions < 98) return null;

	if (configLoading) {
		return (
			<Box p={3} display="flex" alignItems="center" gap={1}>
				<CircularProgress size={20} />
				<Typography variant="body2">Loading content editor…</Typography>
			</Box>
		);
	}

	if (configError === 'not-configured') {
		return (
			<Box p={3}>
				<Alert severity="warning" action={<Button size="small" href="#/admin/settings/github">Go to settings</Button>}>
					GitHub settings not configured. Add your PAT at <strong>Settings → GitHub Content Settings</strong>.
				</Alert>
			</Box>
		);
	}

	if (configError) {
		return <Box p={3}><Alert severity="error">Could not load GitHub config: {configError}</Alert></Box>;
	}

	const markdownFile = record.markdownFile ?? `${record.nameid}.md`;

	return (
		<Box sx={{ p: 1 }}>
			<Box display="flex" alignItems="center" gap={1} mb={1}>
				<GitHubIcon fontSize="small" color="action" />
				<Typography variant="caption" color="text.secondary">
					{ghConfig.owner}/{ghConfig.repo} @ {ghConfig.branch}
				</Typography>
			</Box>

			<Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
				<Tab label="Markdown" />
				<Tab label="Files" />
				<Tab label="Project Config" />
			</Tabs>

			{activeTab === 0 && <MarkdownEditor nameid={record.nameid} markdownFile={markdownFile} ghConfig={ghConfig} />}
			{activeTab === 1 && <FileBrowser nameid={record.nameid} ghConfig={ghConfig} />}
			{activeTab === 2 && <ProjectConfigEditor ghConfig={ghConfig} />}
		</Box>
	);
};
